import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2, BarChart3, Plus, Minus, Trophy, Users } from 'lucide-react';
import { Avatar } from '../../components/Avatar';
import { useToast } from '../../components/Toast';
import { atendimentoApi } from '../atendimento/lib/api';
import type { EstatisticasAgente } from '../atendimento/lib/tipos';
import { formatMoeda } from '@fiodecobre/shared';

type Modo = 'hoje' | 'periodo';

interface ContatoAtendido {
  id: number;
  nome: string;
  jid: string;
  telefone: string | null;
  foto_url: string | null;
  conversa_id: number;
  respondido: boolean;
  atendido_por: string | null;
  resposta_automatica: boolean;
  assumido_admin: boolean;
  horario: string | null;
}

interface ContatoDoDia {
  id: number;
  nome: string | null;
  telefone: string | null;
  jid: string | null;
  foto_url: string | null;
  horario: string | null;
}

interface VendaAgente {
  id: number;
  numero: string;
  total: string;
  comprador: string | null;
  cliente_nome: string | null;
  horario: string;
}

function hojeISO(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function inicioMesISO(): string {
  const d = new Date();
  const tz = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' }).format(d);
  return `${tz}-01`;
}

// Agente | Chamados | Vend | Carga | Atend | Novas | Msgs | 1a resp
const COLS = '2fr 1.1fr 0.8fr 0.9fr 0.9fr 0.9fr 0.9fr 1fr';

function fmtTempo(ms: number): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`;
}

// Linha de contato no expand. `destaque` (sem resposta) recebe fundo ambar — distincao
// visual segura pra daltonismo (sem depender de verde/vermelho).
function LinhaContato({ c, destaque, assumido }: { c: ContatoAtendido; destaque?: boolean; assumido?: boolean }) {
  const titulo = c.nome || c.telefone || c.jid;
  // Info do contato, alinhada a esquerda e separada por " - ".
  const contato = c.nome ? `${c.nome} - ${c.telefone || c.jid}` : (c.telefone || c.jid);
  // Status + horario (so nos sem-resposta), tambem separados por " - ".
  // "Assumido por X" = admin/gerente tomou o chamado (DA-111) — neutro, nem mérito nem falha.
  // "Respondido automaticamente" = saiu NF/fly/avaliacao do sistema, mas nenhuma
  // atendente respondeu pessoalmente (nao confundir com cliente sem retorno nenhum).
  const status = assumido && c.atendido_por
    ? `Assumido por ${c.atendido_por}`
    : c.atendido_por
      ? `Atendido por ${c.atendido_por}`
      : c.resposta_automatica
        ? 'Respondido automaticamente'
        : 'Ninguém respondeu';
  const sufixo = (destaque || assumido || c.resposta_automatica) ? ` - ${status}${c.horario ? ` - ${c.horario}` : ''}` : '';
  return (
    <div
      className="flex items-center gap-2 p-1.5 rounded text-[12px]"
      style={{ backgroundColor: destaque ? 'rgba(253, 230, 138, 0.55)' : 'rgba(0,0,0,0.03)', color: '#000' }}
    >
      <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={24} />
      <div className="min-w-0 flex items-center">
        <span className="truncate">{contato}</span>
        {sufixo && <span className="flex-shrink-0 whitespace-nowrap">{sufixo}</span>}
      </div>
    </div>
  );
}

// Cabecalho de sub-secao recolhivel (Atendidos / Recebidos / Vendas) dentro do
// detalhe do agente. Botao "+"/"−" a esquerda; o contador fica visivel mesmo
// recolhido pra dar o panorama num relance.
function SubSecaoHeader({ aberta, onToggle, cor, children }: { aberta: boolean; onToggle: () => void; cor?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1.5 text-[12px] uppercase tracking-wide mt-3 mb-1 w-full text-left hover:opacity-80 transition"
      style={{ background: 'transparent', border: 0, cursor: 'pointer', color: cor || 'inherit' }}
    >
      {aberta
        ? <Minus size={13} strokeWidth={2.5} className="flex-shrink-0" />
        : <Plus size={13} strokeWidth={2.5} className="flex-shrink-0" />}
      <span>{children}</span>
    </button>
  );
}

export function EstatisticasAgentes() {
  const toast = useToast();

  const [modo, setModo] = useState<Modo>('hoje');
  const [dia, setDia] = useState(hojeISO());
  const [from, setFrom] = useState(inicioMesISO());
  const [to, setTo] = useState(hojeISO());
  const [stats, setStats] = useState<EstatisticasAgente[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [contatosDia, setContatosDia] = useState(0);
  const [contatosExpandido, setContatosExpandido] = useState(false);
  const [listaContatos, setListaContatos] = useState<ContatoDoDia[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [expandidoId, setExpandidoId] = useState<number | null>(null);
  const [contatos, setContatos] = useState<ContatoAtendido[]>([]);
  const [carregandoContatos, setCarregandoContatos] = useState(false);
  const [vendas, setVendas] = useState<VendaAgente[]>([]);
  const [carregandoVendas, setCarregandoVendas] = useState(false);
  const [legendaVisivel, setLegendaVisivel] = useState(() => {
    try { return localStorage.getItem('fiodecobre.stats-agentes.legenda') === 'aberta'; } catch { return false; }
  });
  const [secoesAbertas, setSecoesAbertas] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('fiodecobre.stats-agentes.secoes') || '{}'); } catch { return {}; }
  });

  function toggleLegenda() {
    const novo = !legendaVisivel;
    setLegendaVisivel(novo);
    try { localStorage.setItem('fiodecobre.stats-agentes.legenda', novo ? 'aberta' : 'fechada'); } catch {}
  }

  function toggleSecao(secao: string) {
    setSecoesAbertas((prev) => {
      const novo = { ...prev, [secao]: !prev[secao] };
      try { localStorage.setItem('fiodecobre.stats-agentes.secoes', JSON.stringify(novo)); } catch {}
      return novo;
    });
  }

  async function carregar() {
    setCarregando(true);
    try {
      const [r, resumo] = await Promise.all([
        modo === 'hoje'
          ? atendimentoApi.estatisticasHoje(dia)
          : atendimentoApi.estatisticasPeriodo(from, to),
        modo === 'hoje'
          ? atendimentoApi.resumoDia(dia)
          : atendimentoApi.resumoPeriodo(from, to),
      ]);
      setStats(r);
      setContatosDia(resumo.contatos_unicos);
    } catch {
      toast.erro('Falha ao carregar estatisticas');
    } finally {
      setCarregando(false);
    }
  }

  // Modo "Por dia" recarrega ao trocar a data; modo "Periodo" so via botao Atualizar.
  useEffect(() => {
    setExpandidoId(null);
    setContatos([]);
    setVendas([]);
    setContatosExpandido(false);
    setListaContatos([]);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, dia]);

  async function carregarListaContatos() {
    setCarregandoLista(true);
    try {
      const r = modo === 'hoje'
        ? await atendimentoApi.contatosDoDia(dia)
        : await atendimentoApi.contatosDoDiaPeriodo(from, to);
      setListaContatos(r);
    } catch {
      toast.erro('Falha ao carregar contatos do dia');
    } finally {
      setCarregandoLista(false);
    }
  }

  function toggleContatosDia() {
    const novo = !contatosExpandido;
    setContatosExpandido(novo);
    if (novo) void carregarListaContatos();
  }

  async function expandir(usuarioId: number) {
    if (expandidoId === usuarioId) {
      setExpandidoId(null);
      setContatos([]);
      setVendas([]);
      return;
    }
    setExpandidoId(usuarioId);
    setContatos([]);
    setVendas([]);
    if (modo !== 'hoje') return;
    setCarregandoContatos(true);
    setCarregandoVendas(true);
    try {
      const [rContatos, rVendas] = await Promise.all([
        atendimentoApi.contatosDoAgente(usuarioId, dia),
        atendimentoApi.vendasDoAgente(usuarioId, dia),
      ]);
      setContatos(rContatos);
      setVendas(rVendas);
    } catch {
      toast.erro('Falha ao carregar detalhe do agente');
    } finally {
      setCarregandoContatos(false);
      setCarregandoVendas(false);
    }
  }

  const ordenadas = useMemo(
    () => [...stats].sort((a, b) => {
      const cargaA = a.atribuidos + a.vendas;
      const cargaB = b.atribuidos + b.vendas;
      return cargaB - cargaA || b.atendimentos - a.atendimentos || b.total_mensagens - a.total_mensagens;
    }),
    [stats],
  );
  const totais = useMemo(() => ({
    chamados: ordenadas.reduce((s, x) => s + x.atribuidos, 0),
    vendas: ordenadas.reduce((s, x) => s + x.vendas, 0),
    atendimentos: ordenadas.reduce((s, x) => s + x.atendimentos, 0),
    novas: ordenadas.reduce((s, x) => s + x.conversas_iniciadas, 0),
    mensagens: ordenadas.reduce((s, x) => s + x.total_mensagens, 0),
  }), [ordenadas]);
  const totaisCarga = totais.chamados + totais.vendas;
  // Maior carga do dia (ordenadas ja vem por carga desc). Lider = TODAS que empatam no topo.
  const maxCarga = ordenadas.length ? (ordenadas[0]!.atribuidos + ordenadas[0]!.vendas) : 0;

  return (
    <div style={{ color: '#000' }}>
      <div className="pagina-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={toggleLegenda}
            title={legendaVisivel ? 'Recolher legenda' : 'Ver legenda das colunas'}
            className="flex-shrink-0 rounded transition hover:bg-black/10"
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'inherit', display: 'flex', alignItems: 'center', padding: '2px 4px' }}
          >
            {legendaVisivel
              ? <Minus size={16} strokeWidth={2.5} />
              : <Plus size={16} strokeWidth={2.5} />}
          </button>
          <h2 className="pagina-titulo">
            <BarChart3 size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
            Estatisticas dos Agentes
          </h2>
        </div>
      </div>

      {legendaVisivel && (
        <div className="text-xs mb-3 flex items-start gap-2">
          <BarChart3 size={14} className="mt-0.5 flex-shrink-0" />
          <span><b>Chamados</b> = conversas que o robo atribuiu a ela no dia (round-robin/transferencia automatica; atribuicao manual nao conta), tenha respondido ou nao. Se foi transferida pra outra por falta de resposta, conta pras duas. No detalhe: <b>Atendidos</b> = ela respondeu OU foi resolvido automaticamente pelo sistema (ex: NF/avaliacao); <b>Assumidos pela administração</b> = um admin/gerente entrou e assumiu o chamado (não pesa pra ela — nem como atendido, nem como não atendido); <b>Nao atendidos por ela</b> = repassado pra outra atendente (mostra quem pegou) ou cliente ainda sem retorno. Cada conversa conta uma vez por atendente. <b>Vend</b> = vendas finalizadas como vendedora no periodo. <b>Carga</b> = Chamados + Vend (base do round-robin — quem tem menor carga total recebe o proximo chamado). <b>Atend</b> = conversas distintas em que ela participou. <b>Novas</b> = conversas iniciadas via mensagem inbound nova. <b>Msgs</b> = conversas que ela respondeu. <b>1a resp</b> = tempo medio ate a 1a resposta.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
          {(['hoje', 'periodo'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setModo(m)}
              className="py-1 px-3 rounded text-sm font-medium transition"
              style={{
                backgroundColor: modo === m ? 'var(--panel-bg, #fff)' : 'transparent',
                color: modo === m ? 'var(--page-fg, #000)' : 'inherit',
                boxShadow: modo === m ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
              }}
            >
              {m === 'hoje' ? 'Por dia' : 'Periodo'}
            </button>
          ))}
        </div>

        {modo === 'hoje' && (
          <>
            <input
              type="date"
              value={dia}
              max={hojeISO()}
              onChange={(e) => setDia(e.target.value || hojeISO())}
              className="px-2 py-1 rounded border text-sm"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
            />
            {dia !== hojeISO() && (
              <button
                type="button"
                onClick={() => setDia(hojeISO())}
                className="btn-secundario text-xs"
              >
                Hoje
              </button>
            )}
          </>
        )}

        {modo === 'periodo' && (
          <>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1 rounded border text-sm"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
            />
            <span className="text-xs">ate</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1 rounded border text-sm"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
            />
            <button
              type="button"
              onClick={carregar}
              disabled={carregando}
              className="btn-secundario text-xs disabled:opacity-50"
            >
              {carregando ? <Loader2 size={12} className="animate-spin" /> : 'Atualizar'}
            </button>
          </>
        )}

        <div className="ml-auto text-[11px]">
          Total: <b>{totais.chamados}</b> chamados · <b>{totais.vendas}</b> vend · <b>{totaisCarga}</b> carga · <b>{totais.atendimentos}</b> atend · <b>{totais.novas}</b> novas · <b>{totais.mensagens}</b> msgs
        </div>
      </div>

      {/* Contatos do dia — alinhado a esquerda, logo acima da tabela (sobre AGENTE).
          Botao "+" expande quem sao os contatos. */}
      {!carregando && (
        <div className="mb-2">
          <button
            type="button"
            onClick={toggleContatosDia}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-bold transition hover:brightness-95"
            style={{ backgroundColor: '#fde68a', color: '#000' }}
            title="Numeros distintos que enviaram mensagem (1 por numero, mesmo com varias mensagens). Clique pra ver quem."
          >
            {contatosExpandido
              ? <Minus size={16} strokeWidth={2.5} />
              : <Plus size={16} strokeWidth={2.5} />}
            <Users size={15} />
            Contatos {modo === 'hoje' ? 'do dia' : 'no periodo'}: {contatosDia}
          </button>

          {contatosExpandido && (
            <div
              className="mt-1.5 rounded-lg overflow-y-auto"
              style={{ maxHeight: 300, border: '1px solid var(--border, rgba(0,0,0,0.1))', backgroundColor: 'var(--panel-bg, #fff)' }}
            >
              {carregandoLista ? (
                <div className="flex items-center justify-center py-3 opacity-70">
                  <Loader2 size={14} className="animate-spin" />
                </div>
              ) : listaContatos.length === 0 ? (
                <div className="text-xs p-2.5">Nenhum contato registrado.</div>
              ) : (
                <div className="flex flex-col">
                  {listaContatos.map((c) => {
                    const titulo = c.nome || c.telefone || c.jid || '?';
                    const contato = c.nome ? `${c.nome} - ${c.telefone || c.jid || ''}` : (c.telefone || c.jid || '—');
                    return (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 px-2.5 py-1.5 text-[12px]"
                        style={{ borderBottom: '1px solid var(--border, rgba(0,0,0,0.06))', color: '#000' }}
                      >
                        <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={24} />
                        <span className="truncate">{contato}</span>
                        {c.horario && (
                          <span className="ml-auto flex-shrink-0 whitespace-nowrap opacity-70">{c.horario}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}

      {!carregando && ordenadas.length === 0 && (
        <div className="text-center py-8 text-sm">Sem dados pro periodo.</div>
      )}

      {!carregando && ordenadas.length > 0 && (
        <div className="space-y-1 overflow-y-auto pr-1" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          <div className="grid gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wide sticky top-0" style={{ backgroundColor: 'var(--page-bg, #fff)', gridTemplateColumns: COLS }}>
            <div>Agente</div>
            <div className="text-right">Chamados</div>
            <div className="text-right">Vend</div>
            <div className="text-right">Carga</div>
            <div className="text-right">Atend</div>
            <div className="text-right">Novas</div>
            <div className="text-right">Msgs</div>
            <div className="text-right">1a resp</div>
          </div>
          {ordenadas.map((s) => {
            const expandido = expandidoId === s.usuario_id;
            const tempoMedio = s.qtd_primeira_resp > 0 ? Math.round(Number(s.tempo_primeira_resp_ms) / s.qtd_primeira_resp) : 0;
            const carga = s.atribuidos + s.vendas;
            const lider = carga > 0 && carga === maxCarga;
            return (
              <div key={s.usuario_id} className="rounded-lg" style={{ backgroundColor: expandido ? 'rgba(0,0,0,0.04)' : 'transparent', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
                <button
                  type="button"
                  onClick={() => expandir(s.usuario_id)}
                  className="w-full grid gap-2 px-3 py-2 text-left hover:bg-black/5 rounded-lg transition items-center"
                  style={{ gridTemplateColumns: COLS }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {modo === 'hoje'
                      ? (expandido
                          ? <Minus size={18} strokeWidth={2.5} className="flex-shrink-0" style={{ color: '#000' }} />
                          : <Plus size={18} strokeWidth={2.5} className="flex-shrink-0" style={{ color: '#000' }} />)
                      : <span className="w-3" />}
                    <Avatar nome={s.nome} fotoUrl={s.foto_url} tamanho={28} />
                    <span className="text-sm font-medium truncate">{s.nome}</span>
                    {/* Saiu da loja mas trabalhou no periodo (DA-397): fica no relatorio, marcada.
                        Cinza, nao laranja — "nao trabalha mais aqui" e fato, nao pendencia. */}
                    {s.ativo === false && (
                      <span className="flex-shrink-0 text-[11px]" style={{ color: 'var(--text-muted)' }}>(inativa)</span>
                    )}
                    {lider && (
                      <span
                        className="flex-shrink-0 inline-flex items-center justify-center rounded-full"
                        style={{ backgroundColor: 'var(--accent)', width: 22, height: 22 }}
                        title="Lider do dia (maior carga)"
                      >
                        <Trophy size={13} style={{ color: '#fde047' }} />
                      </span>
                    )}
                  </div>
                  <div className="text-right text-sm">{s.atribuidos}</div>
                  <div className="text-right text-sm">{s.vendas}</div>
                  <div className="text-right text-sm font-bold">{carga}</div>
                  <div className="text-right text-sm font-medium">{s.atendimentos}</div>
                  <div className="text-right text-sm">{s.conversas_iniciadas}</div>
                  <div className="text-right text-sm">{s.total_mensagens}</div>
                  <div className="text-right text-xs">{fmtTempo(tempoMedio)}</div>
                </button>

                {expandido && modo === 'hoje' && (() => {
                  const sufixo = dia === hojeISO() ? 'hoje' : `em ${dia.split('-').reverse().join('/')}`;
                  const atendidos = contatos.filter((c) => c.respondido);
                  // Assumidos pela administracao (DA-111): neutro — fora de "nao atendidos".
                  const assumidosAdmin = contatos.filter((c) => !c.respondido && c.assumido_admin);
                  const semResposta = contatos.filter((c) => !c.respondido && !c.assumido_admin);
                  const carregando_ = carregandoContatos || carregandoVendas;
                  return (
                    <div className="px-3 pb-3 border-t" style={{ borderColor: 'var(--border, rgba(0,0,0,0.08))' }}>
                      {carregando_ ? (
                        <div className="flex items-center justify-center py-3 opacity-70">
                          <Loader2 size={14} className="animate-spin" />
                        </div>
                      ) : (
                        <>
                          {/* Linha-resumo da Carga */}
                          <div className="text-[11px] mt-2 mb-2 opacity-60">
                            Carga <b>{carga}</b> = <b>{s.atribuidos}</b> chamado{s.atribuidos !== 1 ? 's' : ''} + <b>{vendas.length}</b> venda{vendas.length !== 1 ? 's' : ''}
                          </div>

                          {contatos.length === 0 && vendas.length === 0 ? (
                            <div className="text-xs py-1">Nenhum chamado ou venda registrado.</div>
                          ) : (
                            <>
                              {contatos.length > 0 && (
                                <>
                                  <SubSecaoHeader aberta={!!secoesAbertas.atendidos} onToggle={() => toggleSecao('atendidos')}>
                                    Atendidos {sufixo} ({atendidos.length})
                                  </SubSecaoHeader>
                                  {secoesAbertas.atendidos && (
                                    atendidos.length === 0 ? (
                                      <div className="text-xs py-1 opacity-70 pl-5">Nenhum respondido ainda.</div>
                                    ) : (
                                      <div className="flex flex-col gap-1.5 pl-5">
                                        {atendidos.map((c) => <LinhaContato key={c.conversa_id} c={c} />)}
                                      </div>
                                    )
                                  )}

                                  {/* Assumidos pela administracao (DA-111): categoria NEUTRA — admin/gerente
                                      tomou o chamado. Sem cor de alerta (nao e falha da atendente). */}
                                  {assumidosAdmin.length > 0 && (
                                    <>
                                      <SubSecaoHeader aberta={!!secoesAbertas.assumidos} onToggle={() => toggleSecao('assumidos')}>
                                        Assumidos pela administração ({assumidosAdmin.length})
                                      </SubSecaoHeader>
                                      {secoesAbertas.assumidos && (
                                        <div className="flex flex-col gap-1.5 pl-5">
                                          {assumidosAdmin.map((c) => <LinhaContato key={c.conversa_id} c={c} assumido />)}
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {semResposta.length > 0 && (
                                    <>
                                      <SubSecaoHeader aberta={!!secoesAbertas.recebidos} onToggle={() => toggleSecao('recebidos')} cor="var(--primary)">
                                        Recebidos — não atendidos por ela ({semResposta.length})
                                      </SubSecaoHeader>
                                      {secoesAbertas.recebidos && (
                                        <div className="flex flex-col gap-1.5 pl-5">
                                          {semResposta.map((c) => <LinhaContato key={c.conversa_id} c={c} destaque />)}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}

                              {/* Seção Vendas */}
                              <SubSecaoHeader aberta={!!secoesAbertas.vendas} onToggle={() => toggleSecao('vendas')}>
                                Vendas ({vendas.length})
                              </SubSecaoHeader>
                              {secoesAbertas.vendas && (
                                vendas.length === 0 ? (
                                  <div className="text-xs py-1 opacity-70 pl-5">Nenhuma venda no dia.</div>
                                ) : (
                                  <div className="flex flex-col gap-1.5 pl-5">
                                    {vendas.map((v) => {
                                      const cliente = v.cliente_nome || v.comprador || '—';
                                      return (
                                        <div
                                          key={v.id}
                                          className="flex items-center gap-2 p-1.5 rounded text-[12px]"
                                          style={{ backgroundColor: 'rgba(0,0,0,0.03)', color: '#000' }}
                                        >
                                          <span className="font-mono opacity-70 flex-shrink-0">{v.numero}</span>
                                          <span className="truncate flex-1">{cliente}</span>
                                          <span className="flex-shrink-0 font-medium">{formatMoeda(v.total)}</span>
                                          <span className="flex-shrink-0 opacity-60">{v.horario}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
                {expandido && modo === 'periodo' && (
                  <div className="px-3 pb-2 text-[11px]">
                    Drilldown de contatos so disponivel em "Hoje".
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
